import React from 'react';
import uniqid from 'uniqid';
import { Sprite } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { JobResult } from '../../jobs.types';
import {
  ABTEILUNG_ATTRIBUTE_ID,
  ARBEITSORT_ATTRIBUTE_ID,
  RUBRIK_ATTRIBUTE_ID,
} from '../../constants';
import useGetPensumString from './useGetPensumString';

export interface VisitedJob {
  id: string;
  createdAt: string;
}

interface Props {
  job: JobResult;
  newThresholdMs: number;
  visitedJobs: VisitedJob[];
  setVisitedJobs: (visitedJobs: VisitedJob[]) => void;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const JobTeaser = ({ job, newThresholdMs, visitedJobs, setVisitedJobs }: Props) => {
  const { t } = useTranslation('jobs');
  const { id, title, attributes, szas, lastModificationTimestamp, links } = job;

  const pensum = useGetPensumString(szas);

  const url = links.directlink;
  const timestamp = lastModificationTimestamp && new Date(lastModificationTimestamp);

  // remove pensum from title string
  // this regex matches single percentages (80%) or ranges (80 - 100%)
  // with optional spaces, brakets, percent signs and range signs.
  // see work/jobportal/title.txt for example strings and use regex101.com
  // to validate / modify the expression
  const cleanTitle = () =>
    title.replace(
      /(\s)?[(]?(100|[1-9][0-9]?)?(%)?(\s)?(-|–|bis)?(\s)?(100|[1-9][0-9]?)?(\s)?%[)]?/,
      '',
    );

  const isNew = () => {
    if (visitedJobs.some((visitedJob) => visitedJob.id === id)) return false;
    if (!timestamp) return null;
    const now = new Date();
    if (now.getTime() - timestamp.getTime() < newThresholdMs) {
      return true;
    }

    return false;
  };

  const locationAttribute = attributes[ARBEITSORT_ATTRIBUTE_ID] as string[];

  const locations = locationAttribute.join(', ');
  const employmentType = szas.sza_employment_type;
  const startDate = szas.sza_starting_date;

  const metaItems = [
    {
      icon: 'clock',
      text: pensum,
    },
    {
      icon: 'map-marker',
      text: locations,
    },
    {
      icon: 'document',
      text: employmentType,
    },
    {
      icon: 'calendar',
      text: startDate,
    },
  ];

  const formattedDate = new Date(timestamp).toLocaleDateString('de-CH', DATE_OPTIONS);

  const details = `${t('jobteaser_last_changed', {
    date: formattedDate,
  })} - ${attributes[RUBRIK_ATTRIBUTE_ID]}`;

  const markAsVisited = () => {
    if (visitedJobs.some((visitedJob) => visitedJob.id === id)) {
      return;
    }

    setVisitedJobs([...visitedJobs, { id, createdAt: new Date().toISOString() }]);
  };

  return (
    <article className="jobteaser">
      <a
        href={url}
        className="jobteaser__link"
        onClick={markAsVisited}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="jobteaser__details">{details}</div>
        <h2 className="jobteaser__title">
          <span className="jobteaser__title-text">
            {cleanTitle()}
            {isNew() && <span className="jobteaser__new-tag">{t('jobteaser_new')}</span>}
          </span>
        </h2>
        <div className="jobteaser__department">{attributes[ABTEILUNG_ATTRIBUTE_ID]}</div>
        <div className="jobteaser__footer teaser__footer">
          <ul className="jobteaser__meta">
            {metaItems.map((item) => {
              if (!item.text) return null;
              return (
                <li key={uniqid()} className="jobteaser__metaitem">
                  <Sprite
                    id={item.icon}
                    additionalClassNames="jobteaser__metaitem-icon"
                  />
                  <span className="jobteaser__metaitem-label">{item.text}</span>
                </li>
              );
            })}
          </ul>
          <Sprite id="arrow-left" additionalClassNames="jobteaser__footer-arrow" />
        </div>
      </a>
    </article>
  );
};

export default JobTeaser;
